import {connect, IConnectProps} from 'globx-react';
import pointerify, {
    Axis,
    Events,
    EventType,
    Pointerify
} from 'pointerify';
import * as React from 'react';
import {Subject}  from 'rxjs';

import StoreName              from '../../../../State/Shared/Constants/StoreName';
import TimelineModel          from '../../../../State/Stores/Timeline/Timeline';
import * as TestIds           from '../../../Shared/Constants/TestIds';
import UiEventType            from '../../../Shared/Constants/UiEventType';
import IUiEventsStreamPayload from '../../../Shared/Interfaces/IUiEventsStreamPayload';
import composeTokenList       from '../../Util/composeTokenList';
import renderTestId           from '../../Util/renderTestId';

import * as styles from './timeline.css';

interface ITimelineProps extends IConnectProps<TimelineModel> {
    areControlsIdle: boolean;
    uiEvents$: Subject<IUiEventsStreamPayload>;
}

@connect(StoreName.TIMELINE)
class Timeline extends React.Component<ITimelineProps> {
    public pointer: Pointerify;
    public timelineRef: HTMLElement;

    constructor(props) {
        super(props);

        this.handlePointerDrag = this.handlePointerDrag.bind(this);
        this.handlePointerUp = this.handlePointerUp.bind(this);
        this.handlePointerInspect = this.handlePointerInspect.bind(this);
    }

    private get playheadStyle(): React.CSSProperties {
        return {
            left: `${this.props.value.percentProgress}%`
        };
    }

    private get progressStyle(): React.CSSProperties {
        return {
            width: `${this.props.value.percentProgress}%`
        };
    }

    private get inspectStyle(): React.CSSProperties {
        return {
            width: `${this.props.value.percentInspect}%`
        };
    }

    private get inspectTimeStyle(): React.CSSProperties {
        return {
            left: `${this.props.value.percentInspect}%`
        };
    }

    public componentDidMount(): void {
        this.pointer = pointerify(this.timelineRef, {
            behavior: {
                clampAxis: Axis.X
            },
            physics: {
                inertia: false
            }
        });

        this.timelineRef.addEventListener(EventType.POINTER_DRAG, this.handlePointerDrag);
        this.timelineRef.addEventListener(EventType.POINTER_INSPECT, this.handlePointerInspect);
        this.timelineRef.addEventListener(EventType.POINTER_UP, this.handlePointerUp);
    }

    public render(): JSX.Element {
        if (this.pointer) this.pointer.refresh();

        const progressClassName = composeTokenList([
            styles.progress,
            [this.props.value.playback.isAtLiveEdge, styles.progressLive]
        ]);

        const timelineTestId = composeTokenList([
            TestIds.PLAYER_TIMELINE,
            [!this.props.areControlsIdle, TestIds.PLAYER_TIMELINE_VISIBLE]
        ]);

        return (
            <div
                className={styles.timeline}
                data-test-id={renderTestId(timelineTestId)}
                ref={this.setTimelineRef}
            >
                <div
                    style={this.inspectTimeStyle}
                    className={styles.inspectTime}
                    data-time={this.props.value.humanReadableInspectTime}
                />
                <div
                    style={this.inspectStyle}
                    className={styles.inspect}
                    data-test-id={renderTestId(TestIds.PLAYER_TIMELINE_INSPECT)}
                />
                <div
                    style={this.progressStyle}
                    className={progressClassName}
                    data-test-id={renderTestId(TestIds.PLAYER_TIMELINE_PROGRESS)}
                />
                <div
                    style={this.playheadStyle}
                    className={styles.playhead}
                    data-test-id={renderTestId(TestIds.PLAYER_TIMELINE_PLAYHEAD)}
                />
            </div>
        );
    }

    public componentWillUnmount(): void {
        this.timelineRef.removeEventListener(EventType.POINTER_DRAG, this.handlePointerDrag);
        this.timelineRef.removeEventListener(EventType.POINTER_INSPECT, this.handlePointerInspect);
        this.timelineRef.removeEventListener(EventType.POINTER_UP, this.handlePointerUp);

        this.pointer.destroy();
    }

    private setTimelineRef = element => this.timelineRef = element;

    private handlePointerUp(e: Events.PointerUpEvent): void {
        const {multiplierX: multiplier} = e.detail;

        this.props.uiEvents$.next({
            type: UiEventType.TIMELINE_TAP,
            eventData: {
                multiplier
            }
        });
    }

    private handlePointerDrag(e: Events.PointerDragEvent): void {
        const {multiplierX: multiplier} = e.detail;

        this.props.uiEvents$.next({
            type: UiEventType.TIMELINE_SCRUB,
            eventData: {
                multiplier
            }
        });
    }

    private handlePointerInspect(e: Events.PointerInspectEvent): void {
        const {multiplierX: multiplier} = e.detail;

        this.props.uiEvents$.next({
            type: UiEventType.TIMELINE_INSPECT,
            eventData: {
                multiplier
            }
        });
    }
}

export default Timeline;